import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Button, Card, Alert, Spinner, ProgressBar, Modal, Badge, Row, Col, Placeholder } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { pdfApi, vectorStoreApi, dashboardApi } from '../services/api';
import OCRPreviewModal from './OCRPreviewModal';
import PDFViewerEnhanced from './PDFViewerEnhanced';

const STALENESS_BG = { green: '#198754', yellow: '#ffc107', red: '#dc3545', unknown: '#6c757d' };
const STALENESS_LABEL = { green: 'Current', yellow: 'Aging', red: 'Needs Review', unknown: 'No Date' };
const STALENESS_ICON = { green: 'fa-check-circle', yellow: 'fa-exclamation-circle', red: 'fa-times-circle', unknown: 'fa-question-circle' };
const cardMotion = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.28, ease: 'easeOut' },
};

function PDFSkeletonList() {
  return (
    <div className="pdf-list">
      {[0, 1, 2].map((item) => (
        <Card key={item} className="mb-3 pdf-item">
          <Card.Body>
            <Placeholder as={Card.Title} animation="glow" className="mb-3">
              <Placeholder xs={7} />
            </Placeholder>
            <Placeholder as="div" animation="glow" className="mb-2">
              <Placeholder xs={5} />
            </Placeholder>
            <Placeholder as={Card.Text} animation="glow" className="mb-3">
              <Placeholder xs={12} />
              <Placeholder xs={9} />
            </Placeholder>
            <div className="d-flex gap-2">
              <Placeholder.Button variant="secondary" xs={2} />
              <Placeholder.Button variant="secondary" xs={3} />
              <Placeholder.Button variant="secondary" xs={2} />
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

function StalenessChip({ info }) {
  if (!info) return null;
  const { staleness, effective_date, age_years } = info;
  const bg = STALENESS_BG[staleness] || STALENESS_BG.unknown;
  const label = STALENESS_LABEL[staleness] || 'Unknown';
  const icon = STALENESS_ICON[staleness] || 'fa-question-circle';
  const title = effective_date
    ? `Effective: ${effective_date}  ·  Age: ${age_years} yrs`
    : 'No effective date found in document';
  return (
    <span
      title={title}
      className="badge d-inline-flex align-items-center gap-1 me-2"
      style={{
        background: bg,
        color: staleness === 'yellow' ? '#212529' : '#fff',
        fontSize: '0.75rem',
        cursor: 'default',
      }}
    >
      <i className={`fas ${icon}`}></i>
      {label}
      {effective_date && <span style={{ opacity: 0.85, marginLeft: 2 }}>{effective_date}</span>}
    </span>
  );
}

const ManagePDFsTab = () => {
  const [pdfs, setPdfs] = useState([]);
  const [filteredPdfs, setFilteredPdfs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [rebuildProgress, setRebuildProgress] = useState({
    percentage: 0,
    current: 0,
    total: 0,
    message: ''
  });
  const [schedule, setSchedule] = useState({
    enabled: false,
    schedule_date: '',
    schedule_time: '02:00',
    last_run_at: null,
    next_run_at: null,
  });
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pdfToDelete, setPdfToDelete] = useState(null);
  const [showOCRPreview, setShowOCRPreview] = useState(false);
  const [selectedPdfForOCR, setSelectedPdfForOCR] = useState(null);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [selectedPdfForViewer, setSelectedPdfForViewer] = useState(null);
  const [batchResults, setBatchResults] = useState([]);
  const [stalenessMap, setStalenessMap] = useState({});  // filename -> staleness info
  
  const fileInputRef = useRef(null);
  const rebuildPollingRef = useRef(null);

  const filterPDFs = useCallback(() => {
    if (!searchTerm.trim()) {
      setFilteredPdfs(pdfs);
    } else {
      const filtered = pdfs.filter(pdf =>
        pdf.filename.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPdfs(filtered);
    }
  }, [pdfs, searchTerm]);

  useEffect(() => {
    loadPDFs();
    loadVectorstoreSchedule();
    dashboardApi.getStaleness().then(data => {
      const map = {};
      (data.policies || []).forEach(p => { map[p.filename] = p; });
      setStalenessMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    filterPDFs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfs, searchTerm]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (rebuildPollingRef.current) {
        clearInterval(rebuildPollingRef.current);
      }
    };
  }, []);

  const loadPDFs = async () => {
    try {
      setError(null);
      const data = await pdfApi.getPDFs();
      // Ensure that the data is an array before setting it
      if (Array.isArray(data)) {
        setPdfs(data);
      } else {
        // console.warn('API did not return an array for PDFs, received:', data);
        setPdfs([]);
      }
    } catch (error) {
      // console.error('Error loading PDFs:', error);
      // Extract error message from response
      let errorMessage = 'Failed to load PDF list';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setPdfs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadVectorstoreSchedule = async () => {
    try {
      setIsLoadingSchedule(true);
      const data = await vectorStoreApi.getSchedule();
      setSchedule({
        enabled: !!data.enabled,
        schedule_date: data.schedule_date || '',
        schedule_time: data.schedule_time || '02:00',
        last_run_at: data.last_run_at || null,
        next_run_at: data.next_run_at || null,
      });
    } catch (e) {
      // Keep defaults silently if schedule endpoint fails.
    } finally {
      setIsLoadingSchedule(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setIsSavingSchedule(true);
      setError(null);
      const response = await vectorStoreApi.updateSchedule({
        enabled: !!schedule.enabled,
        schedule_date: schedule.schedule_date,
        schedule_time: schedule.schedule_time,
      });
      const updated = response.schedule || {};
      setSchedule(prev => ({
        ...prev,
        enabled: !!updated.enabled,
        schedule_date: updated.schedule_date || prev.schedule_date,
        schedule_time: updated.schedule_time || prev.schedule_time,
        last_run_at: updated.last_run_at || prev.last_run_at,
        next_run_at: updated.next_run_at || prev.next_run_at,
      }));
      setSuccess('Vector store rebuild schedule updated successfully');
    } catch (e) {
      let errorMessage = 'Failed to update rebuild schedule';
      if (e.response?.data?.detail) errorMessage = e.response.data.detail;
      else if (e.response?.data?.message) errorMessage = e.response.data.message;
      else if (e.message) errorMessage = e.message;
      setError(errorMessage);
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const nonPdfs = files.filter(f => f.type !== 'application/pdf');
    if (nonPdfs.length > 0) {
      setError(`Please select only PDF files. Non-PDF: ${nonPdfs.map(f => f.name).join(', ')}`);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);
    setSuccess(null);

    // Local results array mirrors state so we can read final values synchronously
    const localResults = files.map(f => ({ name: f.name, status: 'pending', message: '' }));
    setBatchResults(localResults);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      localResults[i] = { ...localResults[i], status: 'uploading' };
      setBatchResults([...localResults]);

      try {
        const response = await pdfApi.uploadPDF(file);
        const vsUpdated = response.vector_store_updated !== false;
        localResults[i] = {
          ...localResults[i],
          status: vsUpdated ? 'success' : 'warning',
          message: vsUpdated ? 'Uploaded' : 'Uploaded (vector store not updated)'
        };
        successCount++;
      } catch (error) {
        let errorMessage = 'Failed to upload';
        if (error.response?.data?.detail) errorMessage = error.response.data.detail;
        else if (error.response?.data?.message) errorMessage = error.response.data.message;
        else if (error.message) errorMessage = error.message;

        localResults[i] = { ...localResults[i], status: 'error', message: errorMessage };
        failCount++;
      }

      setBatchResults([...localResults]);
      setUploadProgress(Math.round(((i + 1) / files.length) * 100));
    }

    await loadPDFs();

    if (fileInputRef.current) fileInputRef.current.value = '';

    if (files.length === 1) {
      // Single file — clear batch list and show inline message
      if (failCount === 0) {
        setSuccess(`Successfully uploaded ${files[0].name} and added to vector store`);
      } else {
        setError(localResults[0]?.message || 'Upload failed');
      }
      setBatchResults([]);
    } else {
      // Batch — keep results visible; show summary banner
      if (failCount === 0) {
        setSuccess(`Successfully uploaded all ${successCount} files`);
      } else if (successCount === 0) {
        setError(`Failed to upload all ${failCount} files`);
      } else {
        setSuccess(`Uploaded ${successCount} of ${files.length} files. ${failCount} failed — see details below.`);
      }
    }

    setIsUploading(false);
    setTimeout(() => setUploadProgress(0), 1000);
  };

  const confirmDeletePDF = (pdf) => {
    setPdfToDelete(pdf);
    setShowDeleteModal(true);
  };

  const handleDeletePDF = async () => {
    if (!pdfToDelete) return;

    try {
      setError(null);
      setSuccess(null);
      const response = await pdfApi.deletePDF(pdfToDelete.filename);
      
      // Check if vector store was updated
      if (response.vector_store_updated === false) {
        setSuccess(`Successfully deleted ${pdfToDelete.filename}, but vector store update failed. ${response.warning || 'Please rebuild manually.'}`);
      } else {
        setSuccess(`Successfully deleted ${pdfToDelete.filename} and removed from vector store`);
      }
      
      await loadPDFs();
    } catch (error) {
      // console.error('Delete error:', error);
      // Extract error message from response
      let errorMessage = `Failed to delete ${pdfToDelete.filename}`;
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setShowDeleteModal(false);
      setPdfToDelete(null);
    }
  };

  const handleRebuildVectorStore = async () => {
    setIsRebuilding(true);
    setError(null);
    setSuccess(null);
    setRebuildProgress({ percentage: 0, current: 0, total: 0, message: 'Starting rebuild...' });

    try {
      // Start polling immediately for more responsive updates
      const pollStatus = async () => {
        try {
          const status = await vectorStoreApi.getStatus();
          
          if (status.is_building) {
            setRebuildProgress({
              percentage: status.build_progress.percentage || 0,
              current: status.build_progress.current || 0,
              total: status.build_progress.total || 0,
              message: status.build_progress.message || 'Processing...'
            });
          } else if (rebuildPollingRef.current) {
            // Build complete - check if we were building before
            const wasBuilding = status.build_progress.current > 0 || status.build_progress.total > 0;
            if (wasBuilding) {
              clearInterval(rebuildPollingRef.current);
              rebuildPollingRef.current = null;
              setIsRebuilding(false);
              setRebuildProgress({ percentage: 100, current: 0, total: 0, message: 'Complete' });
              setSuccess(`Vector store rebuilt successfully. ${status.document_count} documents indexed.`);
            }
          }
        } catch (pollError) {
          // console.error('Status poll error:', pollError);
          // Continue polling even if one request fails
        }
      };
      
      // Start polling at 500ms intervals for smoother updates
      rebuildPollingRef.current = setInterval(pollStatus, 500);
      
      // Trigger rebuild (async on backend)
      await vectorStoreApi.rebuild();
      
      // Do an immediate poll to get initial state
      pollStatus();
      
    } catch (error) {
      // console.error('Rebuild error:', error);
      // Clear polling if it was started
      if (rebuildPollingRef.current) {
        clearInterval(rebuildPollingRef.current);
        rebuildPollingRef.current = null;
      }
      
      // Extract error message from response
      let errorMessage = 'Failed to rebuild vector store';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setIsRebuilding(false);
      setRebuildProgress({ percentage: 0, current: 0, total: 0, message: '' });
    }
  };

  const handleTestVectorStore = async () => {
    setIsTesting(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await vectorStoreApi.test();
      if (data.status === 'healthy') {
        setSuccess(`Vector store is working properly. Found ${data.document_count} documents.`);
      } else {
        setError('Vector store test failed');
      }
    } catch (error) {
      // console.error('Test error:', error);
      // Extract error message from response
      let errorMessage = 'Failed to test vector store';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsTesting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const renderPDFItem = (pdf, index) => {
    return (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, delay: Math.min(index * 0.03, 0.24) }}
        whileHover={{ y: -2 }}
      >
      <Card className="mb-3 pdf-item fade-in">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <Card.Title className="pdf-title d-flex align-items-center gap-2 flex-wrap">
                <span>
                  <i className="fas fa-file-pdf me-2 text-ut-orange"></i>
                  {pdf.filename}
                </span>
                <StalenessChip info={stalenessMap[pdf.filename]} />
              </Card.Title>
              
              <div className="text-muted small mb-2">
                <i className="fas fa-calendar me-2"></i>
                Uploaded: {new Date(pdf.upload_date).toLocaleDateString()}
                <span className="ms-3">
                  <i className="fas fa-hdd me-2"></i>
                  Size: {formatFileSize(pdf.file_size)}
                </span>
                {pdf.page_count && (
                  <span className="ms-3">
                    <i className="fas fa-file-alt me-2"></i>
                    Pages: {pdf.page_count}
                  </span>
                )}
              </div>
              
              {pdf.preview && (
                <Card.Text className="pdf-preview text-muted">
                  {pdf.preview.length > 200 ? pdf.preview.substring(0, 200) + '...' : pdf.preview}
                </Card.Text>
              )}
            </div>
          </div>
          
          <div className="pdf-actions mt-3">
            <Button
              variant="ut-secondary"
              size="sm"
              onClick={() => {
                setSelectedPdfForViewer(pdf.filename);
                setShowPDFViewer(true);
              }}
              className="me-2"
            >
              <i className="fas fa-eye me-2"></i>View
            </Button>
            
            <Button
              variant="outline-info"
              size="sm"
              onClick={() => {
                setSelectedPdfForOCR(pdf.filename);
                setShowOCRPreview(true);
              }}
              className="me-2"
            >
              <i className="fas fa-search-plus me-2"></i>OCR Preview
            </Button>
            
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => confirmDeletePDF(pdf)}
            >
              <i className="fas fa-trash me-2"></i>Delete
            </Button>
          </div>
        </Card.Body>
      </Card>
      </motion.div>
    );
  };

  return (
    <div className="manage-pdfs-workspace">
      <div className="mb-4">
        <h4 className="text-ut-blue mb-3">
          <i className="fas fa-file-pdf me-2"></i>
          Manage PDF Documents
        </h4>
        <p className="text-muted mb-0">
          Upload, view, and manage your policy documents. After uploading new files, rebuild the vector store to make them searchable.
        </p>
      </div>

      <motion.div {...cardMotion}>
        <Card className="mb-4 manage-pdfs-card">
          <Card.Header className="bg-ut-light">
            <h5 className="mb-0">
              <i className="fas fa-cloud-upload-alt me-2"></i>
              Upload PDFs
            </h5>
          </Card.Header>
          <Card.Body>
            <Form>
              <Form.Group>
                <Form.Label>Select PDF File(s) - hold Ctrl / Cmd to select multiple</Form.Label>
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleFileUpload}
                  disabled={isUploading}
                />
              </Form.Group>
              {isUploading && (
                <div className="mt-3">
                  <ProgressBar
                    now={uploadProgress}
                    label={`${uploadProgress}%`}
                    animated
                    variant="ut-primary"
                    className="mb-2"
                  />
                </div>
              )}
            </Form>

            {batchResults.length > 1 && (
              <div className="mt-3">
                <div className="small fw-semibold mb-2 text-muted">
                  Upload progress ({batchResults.filter(r => r.status === 'success' || r.status === 'warning').length} / {batchResults.length})
                </div>
                <ul className="list-unstyled mb-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {batchResults.map((r, i) => (
                    <li key={i} className="d-flex align-items-center gap-2 mb-1 small">
                      {r.status === 'pending' && <i className="fas fa-clock text-secondary"></i>}
                      {r.status === 'uploading' && <Spinner animation="border" size="sm" variant="primary" />}
                      {r.status === 'success' && <i className="fas fa-check-circle text-success"></i>}
                      {r.status === 'warning' && <i className="fas fa-exclamation-circle text-warning"></i>}
                      {r.status === 'error' && <i className="fas fa-times-circle text-danger"></i>}
                      <span className="text-truncate" style={{ maxWidth: '320px' }} title={r.name}>{r.name}</span>
                      {r.message && <span className="text-muted">- {r.message}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card.Body>
        </Card>
      </motion.div>

      <motion.div {...cardMotion}>
        <Card className="mb-4 manage-pdfs-card">
          <Card.Header className="bg-ut-light">
            <h5 className="mb-0">
              <i className="fas fa-database me-2"></i>
              Vector Store Management
            </h5>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-3">
              The vector store enables semantic search across your documents. Rebuild it after uploading new files.
            </p>

            <div className="d-flex gap-2 mb-3 flex-wrap">
              <Button variant="ut-primary" onClick={handleRebuildVectorStore} disabled={isRebuilding}>
                {isRebuilding ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Rebuilding...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sync me-2"></i>
                    Rebuild Vector Store
                  </>
                )}
              </Button>

              <Button variant="outline-secondary" onClick={handleTestVectorStore} disabled={isTesting}>
                {isTesting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Testing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle me-2"></i>
                    Test Vector Store
                  </>
                )}
              </Button>
            </div>

            {isRebuilding && (
              <div className="mt-2">
                <ProgressBar
                  now={rebuildProgress.percentage}
                  label={`${Math.round(rebuildProgress.percentage)}%`}
                  variant="success"
                  animated
                  className="mb-2"
                />
                <div className="small text-muted">
                  {rebuildProgress.message}
                  {rebuildProgress.total > 0 && (
                    <span className="ms-2">({rebuildProgress.current} / {rebuildProgress.total})</span>
                  )}
                </div>
              </div>
            )}

            <hr className="my-4" />
            <h6 className="mb-3">
              <i className="fas fa-clock me-2"></i>
              Scheduled Rebuild
            </h6>

            {isLoadingSchedule ? (
              <div>
                <Placeholder as="div" animation="glow" className="mb-2">
                  <Placeholder xs={5} />
                </Placeholder>
                <Placeholder as="div" animation="glow" className="mb-2">
                  <Placeholder xs={7} />
                </Placeholder>
              </div>
            ) : (
              <>
                <Form.Check
                  type="switch"
                  id="vectorstore-schedule-enabled"
                  className="mb-3"
                  label="Enable automatic vector store rebuild"
                  checked={!!schedule.enabled}
                  onChange={(e) => setSchedule(prev => ({ ...prev, enabled: e.target.checked }))}
                />
                <Row className="g-2 align-items-end">
                  <Col xs={12} md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Run Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={schedule.schedule_date}
                        onChange={(e) => setSchedule(prev => ({ ...prev, schedule_date: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Run Time</Form.Label>
                      <Form.Control
                        type="time"
                        step={60}
                        value={schedule.schedule_time}
                        onChange={(e) => setSchedule(prev => ({ ...prev, schedule_time: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} md="auto">
                    <Button variant="outline-primary" onClick={handleSaveSchedule} disabled={isSavingSchedule}>
                      {isSavingSchedule ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Schedule
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
                <div className="small text-muted mt-2">
                  {schedule.last_run_at && <div>Last run: {new Date(schedule.last_run_at).toLocaleString()}</div>}
                  {schedule.next_run_at && schedule.enabled && <div>Next run: {new Date(schedule.next_run_at).toLocaleString()}</div>}
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </motion.div>

      <AnimatePresence mode="wait">
        {error && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {success && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
              <i className="fas fa-check-circle me-2"></i>
              {success}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="manage-pdfs-searchbar mb-3">
        <h5 className="mb-0 me-2">
          Uploaded Documents
          {filteredPdfs.length > 0 && (
            <span className="text-muted ms-2">
              ({filteredPdfs.length} {filteredPdfs.length === pdfs.length ? 'total' : `of ${pdfs.length}`})
            </span>
          )}
        </h5>
        <div className="manage-pdfs-search-input">
          <Form.Control
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <PDFSkeletonList />
      ) : filteredPdfs.length === 0 ? (
        <Alert variant="info">
          <i className="fas fa-info-circle me-2"></i>
          {searchTerm ? 'No documents match your search criteria.' : 'No PDF documents uploaded yet.'}
        </Alert>
      ) : (
        <div className="pdf-list">
          {filteredPdfs.map((pdf, index) => renderPDFItem(pdf, index))}
        </div>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete "{pdfToDelete?.filename}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeletePDF}>
            <i className="fas fa-trash me-2"></i>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <OCRPreviewModal
        show={showOCRPreview}
        onHide={() => {
          setShowOCRPreview(false);
          setSelectedPdfForOCR(null);
        }}
        filename={selectedPdfForOCR}
      />

      <PDFViewerEnhanced
        show={showPDFViewer}
        onHide={() => {
          setShowPDFViewer(false);
          setSelectedPdfForViewer(null);
        }}
        pdfUrl={selectedPdfForViewer ? pdfApi.getPDFUrl(selectedPdfForViewer) : null}
        filename={selectedPdfForViewer || ''}
      />
    </div>
  );
};

export default ManagePDFsTab;
